const AUTH_KEY = "token";
const USER_KEY = "user";
const THEME_KEY = "currentStoreTheme";
const IMPERSONATION_KEY = "impersonation_data";

class Auth {
  deleteToken() {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(THEME_KEY);
    localStorage.removeItem(IMPERSONATION_KEY);

    this.deleteUser();
  }

  deleteUser() {
    localStorage.removeItem(USER_KEY);
  }

  setToken(token) {
    localStorage.setItem(AUTH_KEY, token);
  }

  setUser(user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  getToken() {
    return localStorage.getItem(AUTH_KEY);
  }

  getUser() {
    return JSON.parse(localStorage.getItem(USER_KEY));
  }

  getUserType() {
    // During impersonation the token belongs to a customer account
    if (this.isImpersonating()) return "normal";
    return JSON.parse(localStorage.getItem(USER_KEY))?.is_store_user
      ? "store"
      : "normal";
  }

  isStoreOwner() {
    if (this.isImpersonating()) return false;
    const user = this.getUser();
    if (!user?.is_store_user) return false;
    // Explicit flag (set since multi-user support was added)
    if (user.is_store_owner === true) return true;
    if (user.is_store_owner === false) return false;
    // Legacy session: no flag present — fall back to roles array
    const roles = user?.roles ?? [];
    if (roles.length > 0) return roles.some(r => r === 'store_owner' || r?.name === 'store_owner');
    // Oldest sessions: is_store_user=true but no roles at all → treat as owner
    return true;
  }

  isStoreMember() {
    if (this.isImpersonating()) return false;
    const user = this.getUser();
    if (!user?.is_store_user) return false;
    if (user.is_store_owner === true) return false;
    // Explicit member flag via roles
    return (user?.roles ?? []).some(r => r === 'store_member' || r?.name === 'store_member');
  }

  hasPermission(permission) {
    if (this.isStoreOwner()) return true;
    const perms = this.getUser()?.permissions ?? [];
    return perms.includes(permission);
  }

  // Returns the first route a store user is allowed to visit after login.
  defaultStorePath() {
    const candidates = [
      { permission: 'view_dashboard',    path: '/store/dashboard' },
      { permission: 'view_orders',       path: '/store/orders' },
      { permission: 'view_accounts',     path: '/store/accounts' },
      { permission: 'view_sync_status',  path: '/store/sync-status' },
      { permission: 'view_logs',         path: '/store/logs' },
      { permission: 'view_failed_orders',path: '/store/failed-orders' },
      { permission: 'manage_settings',   path: '/exact/settings' },
      { permission: 'manage_users',      path: '/store/team' },
    ];
    for (const { permission, path } of candidates) {
      if (this.hasPermission(permission)) return path;
    }
    return '/'; // fallback: no permissions assigned yet
  }

  isAuthenticated() {
    return localStorage.getItem(AUTH_KEY) !== null;
  }

  startImpersonation(data) {
    localStorage.setItem(IMPERSONATION_KEY, JSON.stringify({
      original_admin: data.original_admin,
      impersonated_account: data.user
    }));
  }

  stopImpersonation() {
    localStorage.removeItem(IMPERSONATION_KEY);
  }

  isImpersonating() {
    return localStorage.getItem(IMPERSONATION_KEY) != null;
  }

  getImpersonationData() {
    const data = localStorage.getItem(IMPERSONATION_KEY);
    return data ? JSON.parse(data) : null;
  }
}

const instance = new Auth();

export default instance;
